import {
  RESEARCH_STATUS_FILTERS,
  type ResearchRecord,
  type ResearchSort,
  type ResearchStatusFilter,
} from "@pokifolio/contracts";
import { Link } from "@tanstack/react-router";
import {
  type ColumnDef,
  type RowSelectionState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Archive, ExternalLink, Search, Trash2, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { ariaSort } from "../../components/data-table/aria-sort";
import { Pagination } from "../../components/data-table/pagination";
import { SortableHeader } from "../../components/data-table/sortable-header";
import { PageHeader } from "../../components/page-header";
import { ErrorAlert } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Input, Select } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useDebouncedValue } from "../../lib/use-debounced-value";
import { useCurrentUser } from "../auth/session";
import { formatMoney } from "../search/format";
import {
  RESEARCH_STATUS_LABELS,
  RESEARCH_STATUS_TONES,
  STATUS_FILTER_LABELS,
  describeAssessment,
  formatDate,
} from "./format";
import type { ResearchListSearch } from "./list-search";
import { closeRule, deleteRule } from "./manage-rules";
import { type ManageRequest, ManageResearchDialog } from "./research-manage-dialogs";
import { useResearchList } from "./research-queries";
import { ResearchRowActions } from "./research-row-actions";

interface ResearchListPageProps {
  readonly filters: ResearchListSearch;
  readonly onFiltersChange: (next: ResearchListSearch) => void;
}

/** Column titles that sort, and the order each starts in. */
const SORTABLE: Partial<Record<string, { sort: ResearchSort; firstDir: "asc" | "desc" }>> = {
  card: { sort: "card", firstDir: "asc" },
  price: { sort: "price", firstDir: "desc" },
  status: { sort: "status", firstDir: "asc" },
  saved: { sort: "created", firstDir: "desc" },
};

/**
 * Every listing saved from Search (D-52: the whole team sees all research). Find, filter and sort on the server;
 * tick rows to close or delete several at once.
 */
export function ResearchListPage({ filters, onFiltersChange }: ResearchListPageProps) {
  const scope = filters.scope ?? "all";
  const status = filters.status ?? "open";
  const sort = filters.sort ?? "updated";
  const dir = filters.dir ?? "desc";
  const page = filters.page ?? 1;
  const [text, setText] = useState(filters.q ?? "");
  const q = useDebouncedValue(text.trim(), 300);
  const list = useResearchList({ scope, status, q, sort, dir, page });
  const me = useCurrentUser();
  const scopeId = useId();
  const statusId = useId();
  const findId = useId();
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [request, setRequest] = useState<ManageRequest | null>(null);

  // What was typed goes into the address (starting again on page 1) once typing stops.
  const typed = useRef(q);
  useEffect(() => {
    if (typed.current === q) return;
    typed.current = q;
    if (q !== (filters.q ?? ""))
      onFiltersChange({ ...filters, q: q === "" ? undefined : q, page: undefined });
  }, [q, filters, onFiltersChange]);

  // A new page or filter starts with nothing ticked.
  const viewKey = JSON.stringify({ scope, status, q, sort, dir, page });
  const [selectionKey, setSelectionKey] = useState(viewKey);
  if (selectionKey !== viewKey) {
    setSelectionKey(viewKey);
    setSelection({});
  }

  const setScope = (value: string) =>
    onFiltersChange({ ...filters, scope: value === "mine" ? "mine" : undefined, page: undefined });
  const setStatus = (value: string) => {
    const next = value as ResearchStatusFilter;
    onFiltersChange({ ...filters, status: next === "open" ? undefined : next, page: undefined });
  };
  const setSort = (column: ResearchSort, nextDir: "asc" | "desc") =>
    onFiltersChange({
      ...filters,
      sort: column === "updated" ? undefined : column,
      dir: nextDir === "desc" ? undefined : nextDir,
      page: undefined,
    });
  const goToPage = (next: number) => onFiltersChange({ ...filters, page: next > 1 ? next : undefined });
  const filtered = scope !== "all" || status !== "open" || q !== "";

  const columns = useMemo<ColumnDef<ResearchRecord>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() ? "indeterminate" : false)
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(value === true)}
            aria-label="Select all on this page"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(value === true)}
            aria-label={`Select ${row.original.card.name} ${row.original.card.number}: ${row.original.listing.title}`}
          />
        ),
      },
      { id: "card", header: "Card", cell: ({ row }) => <CardCell record={row.original} /> },
      { id: "listing", header: "Listing", cell: ({ row }) => <ListingCell record={row.original} /> },
      { id: "price", header: "Price", cell: ({ row }) => <PriceCell record={row.original} /> },
      { id: "status", header: "Step", cell: ({ row }) => <StepCell record={row.original} /> },
      {
        id: "owner",
        header: "Saved by",
        cell: ({ row }) => (row.original.owner.id === me.user.id ? "You" : row.original.owner.name),
      },
      {
        id: "saved",
        header: "Saved",
        cell: ({ row }) => <span className="text-muted-foreground">{formatDate(row.original.savedAt)}</span>,
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <ResearchRowActions
            record={row.original}
            me={me}
            onClose={(record) => setRequest({ action: "close", records: [record] })}
            onDelete={(record) => setRequest({ action: "delete", records: [record] })}
          />
        ),
      },
    ],
    [me],
  );

  const records = list.data?.data ?? [];
  const table = useReactTable({
    data: records,
    columns,
    getRowId: (record) => record.id,
    getCoreRowModel: getCoreRowModel(),
    state: { rowSelection: selection },
    onRowSelectionChange: setSelection,
    enableRowSelection: true,
    manualPagination: true,
    manualSorting: true,
  });
  const selected = table.getSelectedRowModel().rows.map((r) => r.original);
  const closable = selected.filter((r) => closeRule(r, me).allowed);
  const deletable = selected.filter((r) => deleteRule(r, me).allowed);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Research"
        description="Listings saved from Search. Open one to check its condition and price, and ask for approval."
        actions={
          <Button asChild>
            <Link to="/search">
              <Search aria-hidden="true" />
              New search
            </Link>
          </Button>
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-56 flex-1 flex-col gap-1 sm:max-w-sm">
          <label htmlFor={findId} className="text-xs font-medium text-muted-foreground">
            Find research
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id={findId}
              type="search"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Card, set, listing or website"
              className="ps-8"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={scopeId} className="text-xs font-medium text-muted-foreground">
            Show
          </label>
          <Select id={scopeId} value={scope} onChange={(e) => setScope(e.target.value)} className="w-48">
            <option value="all">Everyone’s research</option>
            <option value="mine">My research</option>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={statusId} className="text-xs font-medium text-muted-foreground">
            Step
          </label>
          <Select id={statusId} value={status} onChange={(e) => setStatus(e.target.value)} className="w-52">
            {RESEARCH_STATUS_FILTERS.map((value) => (
              <option key={value} value={value}>
                {STATUS_FILTER_LABELS[value]}
              </option>
            ))}
          </Select>
        </div>
        {filtered && (
          <Button
            variant="ghost"
            onClick={() => {
              setText("");
              // Keeps the sorting; clears what is found and shown.
              onFiltersChange({
                ...(filters.sort === undefined ? {} : { sort: filters.sort }),
                ...(filters.dir === undefined ? {} : { dir: filters.dir }),
              });
            }}
          >
            Reset
            <X aria-hidden="true" />
          </Button>
        )}
      </div>

      {selected.length > 0 && (
        <div
          role="toolbar"
          aria-label="Selected research"
          className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm"
        >
          <span className="me-auto font-medium text-foreground" aria-live="polite">
            {selected.length} selected
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={closable.length === 0}
            title={closable.length === 0 ? "None of these can be closed now." : undefined}
            onClick={() => setRequest({ action: "close", records: closable })}
          >
            <Archive aria-hidden="true" />
            Close {closable.length === selected.length ? "" : `${String(closable.length)} of them`}
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={deletable.length === 0}
            title={deletable.length === 0 ? "None of these can be deleted." : undefined}
            onClick={() => setRequest({ action: "delete", records: deletable })}
          >
            <Trash2 aria-hidden="true" />
            Delete {deletable.length === selected.length ? "" : `${String(deletable.length)} of them`}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelection({})}>
            Clear selection
          </Button>
        </div>
      )}

      {list.isPending ? (
        <Card className="flex flex-col gap-3" aria-label="Loading research">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
          <span className="sr-only">Loading research…</span>
        </Card>
      ) : list.isError ? (
        <ErrorAlert error={list.error} />
      ) : list.data.total === 0 ? (
        <Card className="flex flex-col items-center gap-2 py-12 text-center">
          {filtered ? (
            <p className="text-sm text-muted-foreground">No research matches these filters.</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nothing saved yet.{" "}
              <Link to="/search" className="font-medium text-primary hover:underline">
                Search for a card
              </Link>{" "}
              and click “Save to Research” on a listing.
            </p>
          )}
        </Card>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <Table className="min-w-[960px]">
              <caption className="sr-only">Saved research</caption>
              <TableHeader>
                {table.getHeaderGroups().map((group) => (
                  <TableRow key={group.id} className="hover:bg-transparent">
                    {group.headers.map((header) => {
                      const sortable = SORTABLE[header.id];
                      const active = sortable?.sort === sort;
                      return (
                        <TableHead
                          key={header.id}
                          scope="col"
                          aria-sort={sortable === undefined ? undefined : ariaSort(active, dir)}
                          className="text-xs uppercase tracking-wide text-muted-foreground"
                        >
                          {sortable !== undefined ? (
                            <SortableHeader
                              label={String(header.column.columnDef.header)}
                              column={sortable.sort}
                              sort={sort}
                              dir={dir}
                              firstDir={sortable.firstDir}
                              onSort={setSort}
                            />
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} data-state={row.getIsSelected() ? "selected" : undefined}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="align-top whitespace-normal">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={list.data.page}
            pageSize={list.data.pageSize}
            total={list.data.total}
            busy={list.isPlaceholderData}
            onPage={goToPage}
            noun="research"
          />
        </>
      )}

      <ManageResearchDialog
        request={request}
        onDone={() => {
          setRequest(null);
          setSelection({});
        }}
      />
    </div>
  );
}

function CardCell({ record }: { record: ResearchRecord }) {
  const { card } = record;
  return (
    <div className="flex gap-3">
      {card.imageSmall === null ? (
        <div className="h-14 w-10 shrink-0 rounded bg-muted" aria-hidden="true" />
      ) : (
        <img src={card.imageSmall} alt="" loading="lazy" className="h-14 w-10 shrink-0 object-contain" />
      )}
      <div className="flex flex-col gap-0.5">
        <Link
          to="/research/$researchId"
          params={{ researchId: record.id }}
          className="text-sm font-medium text-foreground hover:underline"
        >
          {card.name} {card.number}
        </Link>
        <span className="text-xs text-muted-foreground">{card.setName}</span>
      </div>
    </div>
  );
}

function ListingCell({ record }: { record: ResearchRecord }) {
  const { listing } = record;
  return (
    <div className="max-w-xs">
      <a
        href={listing.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-start gap-1 text-sm text-foreground hover:underline"
      >
        {listing.title}
        <ExternalLink className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span className="sr-only">(opens in a new tab)</span>
      </a>
      <p className="text-xs text-muted-foreground">{listing.website}</p>
    </div>
  );
}

function PriceCell({ record }: { record: ResearchRecord }) {
  const { listing } = record;
  return (
    <div className="text-sm">
      {listing.price === null ? (
        <span className="text-muted-foreground">Not known</span>
      ) : (
        <span className="font-medium text-foreground">
          {listing.listingType === "auction" ? "Bid " : ""}
          {formatMoney(listing.price)}
        </span>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        {record.price === null
          ? "Price not checked"
          : `Checked: ${formatMoney({ amount: record.price.totalUsd, currency: "USD" })} with shipping`}
      </p>
    </div>
  );
}

function StepCell({ record }: { record: ResearchRecord }) {
  return (
    <div>
      <Badge tone={RESEARCH_STATUS_TONES[record.status]}>{RESEARCH_STATUS_LABELS[record.status]}</Badge>
      <p className="mt-1 text-xs text-muted-foreground">
        {record.assessment === null
          ? "Condition not checked"
          : `Condition: ${describeAssessment(record.assessment)}`}
      </p>
    </div>
  );
}
